import { ExternalLink, Navigation } from 'lucide-react';
import { useOpenWebViewLink } from '@/core/hooks/useOpenWebViewLink';
import {
  buildMapDirectionsUrl,
  buildMapPlaceUrl,
  getMapOverlayAddress,
  getMapOverlayTitle,
} from '../utils/mapEmbed';
import type { PhysicalStoreVm } from '../viewModels/physicalStore.vm';

type StoreMapOverlayProps = {
  store: PhysicalStoreVm;
};

export function StoreMapOverlay({ store }: StoreMapOverlayProps) {
  const { createClickHandler, isApp } = useOpenWebViewLink();
  const title = getMapOverlayTitle(store);
  const addressSnippet = getMapOverlayAddress(store);
  const placeUrl = buildMapPlaceUrl(store);
  const directionsUrl = buildMapDirectionsUrl(store);
  const reviewsUrl = store.storeReviewLink?.trim() || null;

  return (
    <div className="pointer-events-none absolute left-3 top-3 z-10 max-w-[280px] md:left-4 md:top-4 md:max-w-[300px]">
      <div className="pointer-events-auto overflow-hidden rounded-sm bg-white shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
        <div className="px-3 py-2.5">
          <p className="truncate text-sm font-medium text-gray-900">{title}</p>
          {addressSnippet ? (
            <p className="mt-0.5 truncate text-xs text-gray-600">{addressSnippet}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2">
          {placeUrl ? (
            <a
              href={placeUrl}
              target={isApp ? undefined : '_blank'}
              rel={isApp ? undefined : 'noreferrer'}
              onClick={isApp ? createClickHandler(placeUrl) : undefined}
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-gray-200 text-[#1a73e8] hover:bg-gray-50"
              aria-label="View larger map"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : null}
          {directionsUrl ? (
            <a
              href={directionsUrl}
              target={isApp ? undefined : '_blank'}
              rel={isApp ? undefined : 'noreferrer'}
              onClick={isApp ? createClickHandler(directionsUrl) : undefined}
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-gray-200 text-[#1a73e8] hover:bg-gray-50"
              aria-label="Get directions"
            >
              <Navigation className="h-3.5 w-3.5" aria-hidden />
            </a>
          ) : null}
        </div>

        {reviewsUrl ? (
          <div className="border-t border-gray-100 px-3 py-2">
            <a
              href={reviewsUrl}
              target={isApp ? undefined : '_blank'}
              rel={isApp ? undefined : 'noopener noreferrer nofollow'}
              onClick={isApp ? createClickHandler(reviewsUrl) : undefined}
              className="text-xs text-[#1a73e8] hover:underline"
            >
              View Google reviews
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
